// 학부모용 알림장 페이지

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Chip,
  Badge,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonIcon from '@mui/icons-material/Person';
import { useSupabaseParentAuth } from '../hooks/useSupabaseParentAuth';
import { getAccessToken } from '../../../lib/authUtils';
import { API_BASE_URL } from '../../../config/api';

// 게시글 타입
interface Post {
  id: string;
  title: string;
  content: string;
  organization_name: string;
  created_at: string;
  comment_count: number;
  is_read: boolean;
}

// 자녀별 게시글 그룹 타입
interface ChildPostGroup {
  student_id: string;
  student_name: string;
  unread_count: number;
  posts: Post[];
}

// 댓글 타입
interface Comment {
  id: string;
  content: string;
  author_name: string;
  author_type: 'parent' | 'admin';
  created_at: string;
}

// 게시글 상세 타입
interface PostDetail extends Post {
  comments: Comment[];
}

// 탭 패널 컴포넌트
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`notice-tabpanel-${index}`}
      aria-labelledby={`notice-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function NoticeBoardPage() {
  const { parent } = useSupabaseParentAuth();

  // 탭 상태
  const [tabValue, setTabValue] = useState(0);

  // 데이터 상태
  const [announcements, setAnnouncements] = useState<Post[]>([]);
  const [childrenPosts, setChildrenPosts] = useState<ChildPostGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // 확장된 자녀 목록
  const [expandedChildren, setExpandedChildren] = useState<Set<string>>(new Set());

  // 모달 상태
  const [selectedPost, setSelectedPost] = useState<PostDetail | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPostLoading, setIsPostLoading] = useState(false);

  // 댓글 입력 상태
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  // 안읽은 공지 수
  const announcementUnreadCount = announcements.filter((p) => !p.is_read).length;
  const childrenUnreadCount = childrenPosts.reduce((sum, child) => sum + child.unread_count, 0);

  useEffect(() => {
    if (parent?.id) {
      loadPosts();
    }
  }, [parent?.id]);

  const loadPosts = async () => {
    setIsLoading(true);
    setError('');

    try {
      const accessToken = await getAccessToken();
      

      if (!accessToken) {
        setError('로그인이 필요합니다.');
        setIsLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/parent/posts`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '알림장 조회 실패');
      }

      const result = await response.json();
      const data = result.data || result; // API가 { success, data } 또는 직접 데이터를 반환할 수 있음

      setAnnouncements(data.announcements || []);
      setChildrenPosts(data.by_children || []);

      // 첫 번째 자녀를 기본으로 확장
      if (data.by_children && data.by_children.length > 0) {
        setExpandedChildren(new Set([data.by_children[0].student_id]));
      }
    } catch (err: any) {
      console.error('알림장 로드 오류:', err);
      setError(err.message || '알림장을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadPostDetail = async (postId: string) => {
    setIsPostLoading(true);

    try {
      const accessToken = await getAccessToken();
      

      if (!accessToken) {
        setError('로그인이 필요합니다.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/parent/posts/${postId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '게시글 조회 실패');
      }

      const result = await response.json();
      const postData = result.data || result;

      // API 응답 형식에 맞게 변환
      const selectedPostData = {
        ...postData.post,
        comments: postData.comments || [],
      };
      setSelectedPost(selectedPostData);

      // 읽음 처리 후 목록 업데이트
      setAnnouncements((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, is_read: true } : p))
      );
      setChildrenPosts((prev) =>
        prev.map((child) => ({
          ...child,
          posts: child.posts.map((p) => (p.id === postId ? { ...p, is_read: true } : p)),
          unread_count: child.posts.filter((p) => p.id !== postId && !p.is_read).length,
        }))
      );
    } catch (err: any) {
      console.error('게시글 상세 로드 오류:', err);
      setError(err.message || '게시글을 불러오는데 실패했습니다.');
    } finally {
      setIsPostLoading(false);
    }
  };

  const handlePostClick = (post: Post) => {
    setIsModalOpen(true);
    loadPostDetail(post.id);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPost(null);
    setNewComment('');
  };

  const handleSubmitComment = async () => {
    if (!selectedPost || !newComment.trim()) return;

    setIsSubmittingComment(true);

    try {
      const accessToken = await getAccessToken();
      

      if (!accessToken) {
        setError('로그인이 필요합니다.');
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/api/v1/parent/posts/${selectedPost.id}/comments`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ content: newComment.trim() }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '댓글 작성 실패');
      }

      const result = await response.json();
      const newCommentData = result.data || result;

      // 댓글 목록에 추가
      setSelectedPost((prev) =>
        prev
          ? {
              ...prev,
              comments: [...prev.comments, newCommentData],
              comment_count: prev.comment_count + 1,
            }
          : null
      );

      // 목록의 댓글 수 업데이트
      const updateCommentCount = (posts: Post[]) =>
        posts.map((p) =>
          p.id === selectedPost.id ? { ...p, comment_count: p.comment_count + 1 } : p
        );

      setAnnouncements(updateCommentCount);
      setChildrenPosts((prev) =>
        prev.map((child) => ({
          ...child,
          posts: updateCommentCount(child.posts),
        }))
      );

      setNewComment('');
    } catch (err: any) {
      console.error('댓글 작성 오류:', err);
      setError(err.message || '댓글 작성에 실패했습니다.');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const toggleChildExpand = (studentId: string) => {
    setExpandedChildren((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date
      .getDate()
      .toString()
      .padStart(2, '0')}`;
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date
      .getDate()
      .toString()
      .padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date
      .getMinutes()
      .toString()
      .padStart(2, '0')}`;
  };

  // 게시글 아이템 컴포넌트
  const PostItem = ({ post, showOrganization = true }: { post: Post; showOrganization?: boolean }) => (
    <Paper
      sx={{
        p: 2,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
        borderLeft: !post.is_read ? '4px solid' : '4px solid transparent',
        borderLeftColor: !post.is_read ? 'error.main' : 'transparent',
      }}
      onClick={() => handlePostClick(post)}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        {!post.is_read && (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'error.main',
              mt: 0.8,
              flexShrink: 0,
            }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography
              variant="body1"
              fontWeight={!post.is_read ? 600 : 400}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {post.title}
            </Typography>
            {!post.is_read && (
              <Chip label="새글" size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            {showOrganization && (
              <Typography variant="caption">{post.organization_name}</Typography>
            )}
            <Typography variant="caption">{formatDate(post.created_at)}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption">{post.comment_count}</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        알림장
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* 탭 */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab
            label={
              <Badge badgeContent={announcementUnreadCount} color="error" max={99}>
                <Box sx={{ px: 1 }}>전체 공지</Box>
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={childrenUnreadCount} color="error" max={99}>
                <Box sx={{ px: 1 }}>우리 아이</Box>
              </Badge>
            }
          />
        </Tabs>
      </Box>

      {/* 전체 공지 탭 */}
      <TabPanel value={tabValue} index={0}>
        {announcements.length === 0 ? (
          <Alert severity="info">등록된 공지가 없습니다.</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {announcements.map((post) => (
              <PostItem key={post.id} post={post} />
            ))}
          </Box>
        )}
      </TabPanel>

      {/* 우리 아이 탭 */}
      <TabPanel value={tabValue} index={1}>
        {childrenPosts.length === 0 ? (
          <Alert severity="info">등록된 자녀별 알림이 없습니다.</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {childrenPosts.map((child) => {
              const isExpanded = expandedChildren.has(child.student_id);

              return (
                <Paper key={child.student_id} sx={{ overflow: 'hidden' }}>
                  {/* 자녀 헤더 */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2,
                      bgcolor: 'grey.50',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'grey.100' },
                    }}
                    onClick={() => toggleChildExpand(child.student_id)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight={600}>
                        {child.student_name}
                      </Typography>
                      <Chip
                        label={child.posts.length}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20 }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {child.unread_count > 0 && (
                        <Badge badgeContent={child.unread_count} color="error" />
                      )}
                      <IconButton size="small">
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Box>

                  {/* 자녀별 게시글 목록 */}
                  <Collapse in={isExpanded}>
                    <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {child.posts.length === 0 ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ p: 2, textAlign: 'center' }}
                        >
                          등록된 알림이 없습니다.
                        </Typography>
                      ) : (
                        child.posts.map((post) => (
                          <PostItem key={post.id} post={post} showOrganization={false} />
                        ))
                      )}
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}
          </Box>
        )}
      </TabPanel>

      {/* 게시글 상세 모달 */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={600}>
            {selectedPost?.title || '게시글'}
          </Typography>
          <IconButton onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {isPostLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedPost ? (
            <Box>
              {/* 게시글 메타 정보 */}
              <Box sx={{ mb: 2, color: 'text.secondary' }}>
                <Typography variant="caption" display="block">
                  {selectedPost.organization_name}
                </Typography>
                <Typography variant="caption">{formatDateTime(selectedPost.created_at)}</Typography>
              </Box>

              {/* 게시글 본문 */}
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  mb: 3,
                }}
              >
                {selectedPost.content}
              </Typography>



            </Box>
          ) : null}
        </DialogContent>



      </Dialog>
    </Box>
  );
}
